import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useA11y } from "@/context/AccessibilityContext";
import { Accessibility, Contrast, Type, X } from "lucide-react";

// Always-available accessibility control, fixed bottom-left on every page.
export default function AccessibilityButton() {
  const { hc, setHc, dys, setDys } = useA11y();
  const [open, setOpen] = useState(false);

  return (
    <div className="fixed bottom-4 left-4 z-[90]">
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 10, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.96 }} transition={{ duration: 0.16 }}
            className="absolute bottom-14 left-0 w-72 rounded-[var(--ns-radius)] bg-[var(--ns-surface)] border border-[var(--ns-border)] shadow-xl p-4"
            data-testid="a11y-panel" role="dialog" aria-label="Accessibility options">
            <div className="flex items-center justify-between mb-2">
              <span className="font-head font-bold text-sm">Accessibility</span>
              <button onClick={() => setOpen(false)} aria-label="Close" className="text-[var(--ns-subtle)] hover:text-[var(--ns-text)]"><X size={16} /></button>
            </div>
            <Row icon={Contrast} label="High-contrast mode" desc="Dark, high-contrast palette"
              checked={hc} onChange={() => setHc(!hc)} testid="a11y-toggle-contrast" />
            <Row icon={Type} label="Dyslexia-friendly font" desc="Atkinson Hyperlegible, wider spacing"
              checked={dys} onChange={() => setDys(!dys)} testid="a11y-toggle-dyslexia" />
          </motion.div>
        )}
      </AnimatePresence>
      <button onClick={() => setOpen((o) => !o)} data-testid="a11y-fab" aria-label="Accessibility options"
        aria-expanded={open}
        className="w-12 h-12 rounded-full grid place-items-center shadow-lg transition-transform hover:scale-105"
        style={{ background: "var(--ns-primary)", color: "#fff" }}>
        <Accessibility size={24} />
      </button>
    </div>
  );
}

function Row({ icon: Icon, label, desc, checked, onChange, testid }) {
  return (
    <button onClick={onChange} data-testid={testid} aria-pressed={checked}
      className="w-full flex items-center justify-between gap-3 py-3 border-b border-[var(--ns-border)] last:border-0 text-left">
      <span className="flex items-start gap-2.5">
        <Icon size={18} className="text-[var(--ns-primary)] mt-0.5 shrink-0" />
        <span>
          <span className="block font-semibold text-sm">{label}</span>
          <span className="block text-xs text-[var(--ns-muted)]">{desc}</span>
        </span>
      </span>
      <span className="w-11 h-6 rounded-full transition-colors shrink-0" style={{ background: checked ? "var(--ns-primary)" : "var(--ns-border)" }}>
        <span className="block w-4 h-4 bg-white rounded-full mt-1 transition-transform" style={{ transform: checked ? "translateX(22px)" : "translateX(4px)" }} />
      </span>
    </button>
  );
}
